import React, { useState } from 'react';
import { Auth } from 'aws-amplify';
import { Row, Card, Form, Input, Typography, Button, message } from 'antd';
import logo from '../../images/loginLogo.svg';

const { Title, Text, Link } = Typography;
const { Password } = Input;

interface IProps {
  authData?: any;
  authState?: any;
  onStateChange?: any;
}

const RequireNewPassword: React.FC<IProps> = (props: IProps) => {
  const { authData, authState, onStateChange } = props;
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();

  /*
   * When the form passes client side validation, we set authState to
   * be confirmSignIn.
   *
   * On any error, we reset the form fields, set the loading state to
   * false and display the error message to the user.
   */
  const onFinish = async (values: any) => {
    try {
      setLoading(true);
      const user = await Auth.completeNewPassword(
        authData,
        values.newPassword,
        [],
      );
      if (user.challengeName === 'SMS_MFA') {
        onStateChange('confirmSignIn', user);
        message.success('Your password has been updated!');
      } else {
        form.resetFields();
        message.error('Looks like something went wrong!');
      }
      setLoading(false);
    } catch (e) {
      form.resetFields();
      setLoading(false);
      message.error(e.message);
    }
  };

  const onLinkClick = () => {
    onStateChange('signIn');
  };

  return authState === 'requireNewPassword' ? (
    <Row justify="center" align="middle" className="Authenticator-page">
      <Card className="Authenticator-card Authenticator-card-fade-in">
        <Form form={form} size="large" onFinish={onFinish}>
          <img src={logo} alt="ehe-logo" className="Authenticator-logo" />
          <Title level={4}>Change Password:</Title>
          <Form.Item
            name="newPassword"
            hasFeedback
            rules={[
              {
                required: true,
                message: 'Please enter your new password!',
              },
              /*
               * The antd built-in form field validator checks the new password and ensures that
               * the current value for new password matches the provided regular expression.
               */
              () => ({
                validator(rule, value) {
                  if (
                    !value ||
                    value.match(
                      `^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[()&.,@?#$/!%+]).{8,}`,
                    )
                  ) {
                    return Promise.resolve();
                  }
                  return Promise.reject(
                    new Error(`Password must contain at least 8 characters, one uppercase and one
                    lowercase character, one number, and one special character - () & .
                    , @ ? # $ / ! % +`),
                  );
                },
              }),
            ]}
          >
            <Password placeholder="Enter New Password..." />
          </Form.Item>
          <Title level={4}>Confirm Change Password:</Title>
          <Form.Item
            name="confirmPassword"
            hasFeedback
            rules={[
              {
                required: true,
                message: 'Please confirm your password!',
              },
              /*
               * This field uses the same built-in antd validator but it is dependant on
               * the current value of the newPassword field. If the values of this field and
               * its dependant match, validation will succeed.
               */
              ({ getFieldValue }) => ({
                validator(rule, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('Passwords do not match!'));
                },
              }),
            ]}
          >
            <Password placeholder="Confirm New Password..." />
          </Form.Item>
          <Row justify="center" className="Authenticator-margin-top">
            <Button type="primary" htmlType="submit" loading={loading}>
              Change Password
            </Button>
          </Row>
          <Row justify="center" className="Authenticator-margin-top">
            <Text>
              Experiencing issues?{' '}
              <Link onClick={onLinkClick}>Back to Sign In</Link>
            </Text>
          </Row>
        </Form>
      </Card>
    </Row>
  ) : null;
};

export default RequireNewPassword;
